import { useCallback } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { sales } from '../api/methods';
export const salesKeys = {
    all: ['sales'],
    documents: (params) => ['sales', 'documents', params],
    document: (type, id) => ['sales', 'document', type, id],
    meta: (type, id) => ['sales', 'meta', type, id],
    attachments: (type, id) => ['sales', 'attachments', type, id],
    drafts: ['sales', 'drafts'],
    draft: (id) => ['sales', 'draft', id],
};
/**
 * Authoritative, paginated estimate/invoice history from the QBO mirror cache.
 * `keepPreviousData` keeps the table stable while paging/filtering.
 */
export function useSalesDocuments(params) {
    return useQuery({
        queryKey: salesKeys.documents(params),
        queryFn: () => sales.listDocuments(params),
        staleTime: 60_000,
        placeholderData: keepPreviousData,
    });
}
/**
 * Build an instant-paint placeholder for a doc detail from the already-cached
 * list row (number/customer/date/total/status). Lets the detail header render
 * the moment the user opens a row, before the full detail (and the live-QBO
 * refresh) land. Returns undefined when the row isn't cached (direct URL hit).
 */
function buildSalesDocPlaceholder(qc, type, id) {
    for (const [, page] of qc.getQueriesData({ queryKey: ['sales', 'documents'] })) {
        const row = page?.docs?.find((d) => d.doc_id === id && d.type === type);
        if (!row)
            continue;
        return {
            type: row.type,
            doc_id: row.doc_id,
            doc_number: row.doc_number,
            customer_name: row.customer_name,
            qb_customer_id: row.qb_customer_id,
            date: row.date,
            secondary_date: null,
            total: row.total,
            balance: row.balance,
            status: row.status,
            bill_city: null,
            bill_state: null,
            ship_city: null,
            ship_state: null,
            synced_at: null,
            qbo_url: row.qbo_url,
            lines: [],
            linked_tickets: [],
            invoiced_by: null,
            source: 'cache',
            fetched_at: null,
        };
    }
    return undefined;
}
/** A single estimate/invoice with parsed line items + linked tickets. */
export function useSalesDocument(type, id, enabled = true) {
    const qc = useQueryClient();
    return useQuery({
        queryKey: salesKeys.document(type, id ?? ''),
        queryFn: () => sales.getDocument(type, id).then((r) => r.doc),
        enabled: enabled && !!id,
        staleTime: 60_000,
        // Seed the header instantly from the cached list row while the full detail
        // and the live-QBO refresh load. Does not change the refresh flow: the page
        // still fires its live-on-open refresh and shows its freshness indicator.
        placeholderData: () => (id ? buildSalesDocPlaceholder(qc, type, id) : undefined),
    });
}
/**
 * Prefetch a sales doc's detail into cache (fired on row hover / tap-start from
 * the list) so opening the doc paints from cache instead of a cold fetch.
 */
export function usePrefetchSalesDocument() {
    const qc = useQueryClient();
    return useCallback((type, id) => {
        if (!id)
            return;
        void qc.prefetchQuery({
            queryKey: salesKeys.document(type, id),
            queryFn: () => sales.getDocument(type, id).then((r) => r.doc),
            staleTime: 60_000,
        });
    }, [qc]);
}
/**
 * Fetch the doc live from QuickBooks and write it into the document query cache,
 * so the page swaps cache→live transparently. Powers "live on open" (fired once
 * on mount) and the "Refresh from QBO" button. Resolves with the live doc plus an
 * optional `live_error` when QBO was unreachable and the cache was used instead.
 * A deleted QBO document is still fail-soft but carries live_unavailable_reason
 * so consumers can suppress stale cached financials.
 */
export function useRefreshSalesDocumentFromQbo() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ type, id }) => sales.getDocument(type, id, { live: true }),
        onSuccess: (resp, { type, id }) => {
            qc.setQueryData(salesKeys.document(type, id), resp.doc);
        },
    });
}
/** Direct same-origin URL for a QBO-rendered sales document PDF download. */
export function useSalesDocumentPdfUrl(type, id) {
    return id ? sales.documentPdfUrl(type, id) : null;
}
/**
 * Email a QBO-rendered estimate/invoice to a recipient through QuickBooks.
 * QBO renders and delivers its own PDF; the doc detail carries no delivery
 * status field, so there's nothing local to invalidate on success.
 */
export function useSendSalesDocEmail() {
    return useMutation({
        mutationFn: ({ type, id, sendTo }) => sales.sendDocumentEmail(type, id, sendTo),
    });
}
/** Manager/Admin QBO write: void a non-ticket invoice from the Sales module. */
export function useVoidSalesInvoice() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (qbInvoiceId) => sales.voidInvoice(qbInvoiceId),
        onSuccess: (_result, qbInvoiceId) => {
            qc.invalidateQueries({ queryKey: salesKeys.all });
            qc.invalidateQueries({ queryKey: salesKeys.document('invoice', qbInvoiceId) });
        },
    });
}
/** Manager/Admin QBO write: delete a non-invoiced estimate from the Sales module. */
export function useDeleteSalesEstimate() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (qbEstimateId) => sales.deleteEstimate(qbEstimateId),
        onSuccess: (_result, qbEstimateId) => {
            qc.invalidateQueries({ queryKey: salesKeys.all });
            qc.invalidateQueries({ queryKey: salesKeys.document('estimate', qbEstimateId) });
        },
    });
}
/** A sales doc's Alturex-local notes + reference links (never synced to QBO). */
export function useSalesDocMeta(type, id, enabled = true) {
    return useQuery({
        queryKey: salesKeys.meta(type, id ?? ''),
        queryFn: () => sales.getMeta(type, id).then((r) => r.meta),
        enabled: enabled && !!id,
        staleTime: 60_000,
    });
}
/** Save a sales doc's local notes/links. */
export function useUpdateSalesDocMeta() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ type, id, data }) => sales.updateMeta(type, id, data).then((r) => r.meta),
        onSuccess: (meta, { type, id }) => {
            qc.setQueryData(salesKeys.meta(type, id), meta);
        },
    });
}
// ── Sales doc attachments (files / photos) ───────────────────────────────────
/** Files/photos staged on a sales doc (paginated). */
export function useSalesDocAttachments(type, id, options = {}) {
    const limit = options.limit ?? 50;
    const offset = options.offset ?? 0;
    return useQuery({
        queryKey: [...salesKeys.attachments(type, id ?? ''), { limit, offset }],
        queryFn: () => sales.getAttachments(type, id, { limit, offset }),
        enabled: !!id,
    });
}
export function useUploadSalesDocAttachment(type, id) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (formData) => sales.uploadAttachment(type, id, formData),
        onSuccess: (att) => {
            qc.setQueriesData({ queryKey: salesKeys.attachments(type, id) }, (old) => old ? { ...old, attachments: [att, ...old.attachments], total: old.total + 1 } : old);
            qc.invalidateQueries({ queryKey: salesKeys.attachments(type, id) });
        },
    });
}
export function useDeleteSalesDocAttachment(type, id) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (attachmentId) => sales.deleteAttachment(type, id, attachmentId),
        onSuccess: (_data, attachmentId) => {
            qc.setQueriesData({ queryKey: salesKeys.attachments(type, id) }, (old) => {
                if (!old)
                    return old;
                const remaining = old.attachments.filter((a) => a.id !== attachmentId);
                return { ...old, attachments: remaining, total: Math.max(0, old.total - (old.attachments.length - remaining.length)) };
            });
            qc.invalidateQueries({ queryKey: salesKeys.attachments(type, id) });
        },
    });
}
// ── Estimate builder (Phase 3) ───────────────────────────────────────────────
/** Whether the estimate builder feature flag is on (gates the builder UI). */
export function useSalesBuilderStatus(enabled = true) {
    return useQuery({
        queryKey: ['sales', 'builder-status'],
        queryFn: () => sales.builderStatus().then((r) => r.enabled),
        enabled,
        staleTime: 5 * 60_000,
    });
}
/** All draft estimates for the realm (org-owned; all Sales/Managers see them). */
export function useSalesDrafts(enabled = true) {
    return useQuery({
        queryKey: salesKeys.drafts,
        queryFn: () => sales.listDrafts().then((r) => r.drafts),
        enabled,
        staleTime: 15_000,
    });
}
/** A single draft for the builder. */
export function useSalesDraft(id, enabled = true) {
    return useQuery({
        queryKey: salesKeys.draft(id ?? ''),
        queryFn: () => sales.getDraft(id).then((r) => r.draft),
        enabled: enabled && !!id,
        staleTime: 5_000,
    });
}
export function useCreateSalesDraft() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: () => sales.createDraft().then((r) => r.draft),
        onSuccess: () => qc.invalidateQueries({ queryKey: salesKeys.drafts }),
    });
}
/** Create an edit-mode draft from a posted estimate (revise → sparse QBO update). */
export function useCreateSalesDraftFromEstimate() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (qbEstimateId) => sales.createDraftFromEstimate(qbEstimateId).then((r) => r.draft),
        onSuccess: () => qc.invalidateQueries({ queryKey: salesKeys.drafts }),
    });
}
/**
 * @param options.getAbortSignal Lets the caller abort an in-flight autosave PUT
 *   at unmount (mirrors `useUpdateTicket`), so a save racing a navigation can't
 *   land its older response over newer draft state (the "silent revert" from #475).
 */
export function useSaveSalesDraft(options) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ id, data }) => sales.saveDraft(id, data, options?.getAbortSignal?.()).then((r) => r.draft),
        onSuccess: (draft) => {
            qc.setQueryData(salesKeys.draft(draft.id), draft);
            qc.invalidateQueries({ queryKey: salesKeys.drafts });
        },
    });
}
export function useDeleteSalesDraft() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (id) => sales.deleteDraft(id),
        onSuccess: () => qc.invalidateQueries({ queryKey: salesKeys.drafts }),
    });
}
/**
 * Post a draft to QuickBooks as an estimate, then refresh the draft + history.
 * `overwriteSyncToken` overwrites a concurrent QBO change on an edit-mode draft:
 * the conflict's `current.base_sync_token`, the version the caller showed the
 * user before offering the overwrite.
 */
export function usePostSalesDraft() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ id, overwriteSyncToken, customerPoId }) => sales.postDraft(id, { overwriteSyncToken, customerPoId }),
        onSuccess: (_res, { id }) => {
            qc.invalidateQueries({ queryKey: salesKeys.draft(id) });
            qc.invalidateQueries({ queryKey: salesKeys.drafts });
            qc.invalidateQueries({ queryKey: salesKeys.all });
        },
    });
}
/** Reload an edit draft from the current live estimate (rebase — see api.reloadDraftFromEstimate). */
export function useReloadSalesDraftFromEstimate() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (id) => sales.reloadDraftFromEstimate(id).then((r) => r.draft),
        onSuccess: (draft) => {
            qc.setQueryData(salesKeys.draft(draft.id), draft);
            qc.invalidateQueries({ queryKey: salesKeys.drafts });
        },
    });
}
//# sourceMappingURL=useSales.js.map